import { useEffect, useState } from "react";
import { X } from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import Router from "next/router";
import { LocalizationProvider, Localized } from "@fluent/react";
import { useElements, useStripe } from "@stripe/react-stripe-js";

import ContainerButton from "@kikoff/components/src/v1/buttons/ContainerButton";
import KButton from "@kikoff/components/src/v1/buttons/KButton";
import useErrorConsumer from "@kikoff/hooks/src/useErrorConsumer";
import { google, web } from "@kikoff/proto/src/protos";
import { webRPC } from "@kikoff/proto/src/rpc";
import { handleFailedStatus, handleProtoStatus } from "@kikoff/utils/src/proto";

import PageAlign from "@component/dashboard/offer/page_align";
import ViewTemplate from "@component/dashboard/offer/view_template";
import { PageRegistration } from "@component/flows/hooks/usePageFlow";
import Line from "@component/line";
import StreamlinedPaymentPromoCard from "@component/onboarding/promo";
import StreamlinedHeader from "@component/onboarding/streamlined_header";
import StreamlinedPaymentMethod from "@component/payment/streamlined";
import ProductEntry from "@component/shopping/product_entry";
import {
  fetchCreditLineAccount,
  setupCreditLineRepayment,
} from "@feature/credit_line";
import { initOnboarding } from "@feature/onboarding";
import { dismiss, selectFeatureFlag } from "@feature/page";
import {
  selectActiveSubscription,
  selectCurrentSubscriptionProduct,
  setCaPaymentSetUpAfterOpen,
  setRedirectedToCaPayment,
} from "@feature/shopping";
import { setSelectedPlan } from "@feature/subscription";
import { selectFirstMonthCheckoutPromo } from "@feature/user";
import { getKikoffCreditAccountMentalModelName } from "@src/constants";
import { track } from "@util/analytics";
import { autoPayState } from "@util/payments";

import cashappLogo from "./assets/images/cashapp-logo.svg";
import {
  DiscountedPayment,
  l10n,
  PersonaPassableDisclaimer,
  PromoDisclaimer,
  StreamlinedAgreementSection,
  StreamlinedAutopaySection,
} from "./streamlined_payment";

import styles from "./open_credit_account_first_payment.module.scss";

interface OpenCreditAccountFirstPaymentProps extends PageRegistration {}

export default function OpenCreditAccountFirstPayment({
  configFlow,
  page,
}: OpenCreditAccountFirstPaymentProps) {
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const error = useErrorConsumer();

  const cashappEnabled = useSelector(selectFeatureFlag("cashapp_stripe_pay"));
  const promo = useSelector(selectFirstMonthCheckoutPromo(true));

  const paymentDueLocaleKey = promo
    ? "store-payment-payment-due-first"
    : "store-payment-payment-due";

  const creditLine = useSelector((state) => state.creditLine.account);
  const [subscription] = useSelector(selectActiveSubscription.load());
  const [product] = useSelector(selectCurrentSubscriptionProduct.load());

  const [paymentMethodToken, setPaymentMethodToken] = useState<string>();
  const [validCardEntry, setValidCardEntry] = useState(false);
  const [autopayEnabled, setAutopayEnabled] = useState(true);
  const [agree, setAgree] = useState(false);
  const [pending, setPending] = useState(false);
  const [scheduledPaymentDate, setScheduledPaymentDate] = useState(null);
  const [
    postOnboardingStatus,
    setPostOnboardingStatus,
  ] = useState<web.public_.CompletionStatusResponse.CompletionStatus>();

  const [submitButtonText, setSubmitButtonText] = useState("Submit payment");
  const [cashappSubmitButtonText, setCashappSubmitButtonText] = useState(
    "Pay with Cash App"
  );

  const paymentButtonsDisabled =
    !agree || (!autopayEnabled && scheduledPaymentDate === null) || pending;
  const submitButtonDisabled =
    !(validCardEntry || paymentMethodToken) || paymentButtonsDisabled;

  const showPassableDisclaimer =
    postOnboardingStatus ===
    web.public_.CompletionStatusResponse.CompletionStatus.PERSONA_PENDING;

  function resetSubmitButton() {
    setSubmitButtonText(autopayEnabled ? "Submit payment" : "Schedule payment");
    setCashappSubmitButtonText("Pay with Cash App");
    setPending(false);
  }

  useEffect(() => {
    resetSubmitButton();
  }, [autopayEnabled]);

  useEffect(() => {
    webRPC.PostBoarding.completionStatus({}).then(
      handleProtoStatus({
        SUCCESS(data) {
          if (
            data.completionStatus ===
            web.public_.CompletionStatusResponse.CompletionStatus.PERSONA_FAILED
          ) {
            dispatch(initOnboarding());
          }
          setPostOnboardingStatus(data.completionStatus);
        },
        _DEFAULT: handleFailedStatus("Failed to get post boarding status"),
      })
    );
  }, []);

  useEffect(() => {
    dispatch(setRedirectedToCaPayment(true));
  }, []);

  const submitPaymentButton = async ({ cashapp = false }) => {
    const setButtonText = cashapp
      ? setCashappSubmitButtonText
      : setSubmitButtonText;

    track.tap("CA - Payment - Submit Payment Button", { cashapp });

    setPending(true);

    setButtonText("Submitting payment...");
    if (autopayEnabled) dispatch(dismiss("AUTOPAY_TERMS"));

    setupCreditLine({
      cashapp,
      methodToken: cashapp ? null : paymentMethodToken,
      setButtonText,
    });
  };

  const setupCreditLine = ({
    cashapp,
    methodToken,
    setButtonText,
  }: {
    cashapp: boolean;
    methodToken: string;
    setButtonText: (text: string) => void;
  }) => {
    dispatch(
      setupCreditLineRepayment({
        autopayEnabled,
        elements,
        onUpdate(text) {
          setButtonText(text);
        },
        openCreditLine: false,
        payCreditLine: autopayEnabled,
        paymentMethodToken: methodToken,
        stripe,
        cashapp,
      })
    )
      .then(async () => {
        track("CA - Payment - Payment Success", { cashapp });

        if (!autopayEnabled) {
          await webRPC.CreditLine.updateCreditLineSettings({
            creditLineToken: creditLine.token,
            autoPay: {
              state: autoPayState(autopayEnabled),
              paymentMethodToken: methodToken,
            },
          });
          await webRPC.CreditLine.createFirstPaymentDate({
            creditLineToken: creditLine.token,
            scheduledAt: google.protobuf.Timestamp.create({
              seconds: scheduledPaymentDate / 1000,
            }),
          });
        }

        window.localStorage.removeItem("first-payment-date");

        await dispatch(setSelectedPlan(subscription?.plan));
        await dispatch(fetchCreditLineAccount());
        await dispatch(setCaPaymentSetUpAfterOpen(true));

        page.controller.next();
      })
      .catch((err) => {
        if (!err.ignore) error.throw(err.message);

        resetSubmitButton();
      });
  };

  const blockDashboard = useSelector(
    selectFeatureFlag("block_dashboard_unless_ca")
  );

  const openCaNeedsFirstPayment =
    blockDashboard &&
    creditLine &&
    creditLine.autopayEnabled &&
    !creditLine.activatedAt;

  useEffect(() => {
    if (!openCaNeedsFirstPayment) {
      page.controller.next();
    }
  }, [openCaNeedsFirstPayment]);

  if (!openCaNeedsFirstPayment) {
    return null;
  }

  return (
    <PageAlign
      className={styles["page-align-no-padding"]}
      scroll
      overscroll={false}
    >
      <LocalizationProvider l10n={l10n}>
        <ViewTemplate className={styles["flat-payment-view"]}>
          <StreamlinedHeader page={page} />
          <div className="row pt-3">
            <div className="expand" />
            <ContainerButton
              onClick={() => {
                Router.replace("/dashboard");
              }}
            >
              <X />
            </ContainerButton>
          </div>

          <h3 data-cy="streamlined-payment-title">
            <Localized id="store-payment-title" />
          </h3>
          <h5 className="color:error">
            There was a payment error. Please try again.
          </h5>
          <div className={styles["flat-wrapper"]}>
            <div className={styles["streamlined-product-entry"]}>
              {product && <ProductEntry product={product} contentOnly />}
              <div className={styles["payment-details"]}>
                <div className="text:large+">
                  <Localized id="payment-details" />
                </div>
                {promo && (
                  <StreamlinedPaymentPromoCard
                    variant="success"
                    className="mt-2"
                    openCaNeedsFirstPayment
                  />
                )}
                <div className={styles["due-now-row"]}>
                  <div className="expand">
                    <Localized id={paymentDueLocaleKey} />
                  </div>
                  <DiscountedPayment product={product} caAlreadyOpened />
                </div>
              </div>
            </div>
            <Line />
            {!cashappEnabled && (
              <StreamlinedPaymentMethod
                setPaymentMethodToken={setPaymentMethodToken}
                setValidCardEntry={setValidCardEntry}
              />
            )}
            <StreamlinedAutopaySection
              autopayEnabled={autopayEnabled}
              scheduledPaymentDate={scheduledPaymentDate}
              setAutopayEnabled={setAutopayEnabled}
              setScheduledPaymentDate={setScheduledPaymentDate}
              creditLine={creditLine}
            />
            <StreamlinedAgreementSection
              agree={agree}
              autopayEnabled={autopayEnabled}
              setAgree={setAgree}
            />

            {cashappEnabled && (
              <div className={paymentButtonsDisabled ? styles.disabled : null}>
                <StreamlinedPaymentMethod
                  setPaymentMethodToken={setPaymentMethodToken}
                  setValidCardEntry={setValidCardEntry}
                />
              </div>
            )}
            <div className={styles["button-wrapper"]}>
              <KButton
                variant="black"
                size="full-width"
                state={submitButtonDisabled || pending ? "disabled" : "active"}
                data-cy="submit-payment-button"
                className={`${styles["submit-payment-button"]} mt-3 text-center`}
                onClick={() => {
                  submitPaymentButton({ cashapp: false });
                }}
              >
                {submitButtonText}
              </KButton>
            </div>

            {cashappEnabled && (
              <div>
                <div className={styles["cashapp-divider"]}>
                  <div className={styles["line"]} />
                  OR
                  <div className={styles["line"]} />
                </div>

                <div className={styles["button-wrapper"]}>
                  <KButton
                    variant="black"
                    size="full-width"
                    state={paymentButtonsDisabled ? "disabled" : "active"}
                    className={`${styles["submit-payment-button"]} text-center `}
                    onClick={() => {
                      submitPaymentButton({ cashapp: true });
                    }}
                  >
                    <div className={styles["cashapp-submit-button"]}>
                      <img src={cashappLogo.src} />
                      {cashappSubmitButtonText}
                    </div>
                  </KButton>
                </div>
              </div>
            )}

            <div className={styles["disclaimer-text"]}>
              <Localized
                id="store-payment-agreement"
                vars={{
                  kikoffCreditAccount: getKikoffCreditAccountMentalModelName(),
                }}
              />
            </div>
            <PromoDisclaimer
              plan={subscription?.plan}
              creditLineAgreement={creditLine.creditLineAgreement}
            />
            {showPassableDisclaimer && <PersonaPassableDisclaimer />}
          </div>
        </ViewTemplate>
      </LocalizationProvider>
    </PageAlign>
  );
}
